import './assets/main.css'

import { createApp, watch } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'
import Ripple from 'primevue/ripple'
import { VueQueryPlugin } from '@tanstack/vue-query'

import App from './App.vue'
import { createAppRouter } from './router'
import { YubiqPreset } from './YubiqPreset'
import { i18n, setupI18n, getCurrentLocale } from './i18n'
import { applyPrimeVueLocale, createPrimeVueLocaleConfig } from './i18n/utils/primeVueLocale'
import { initializeApiClient } from './api/config/client'
import { setupMocks } from './api/mock/server'
import { env, shouldEnableMocks, validateEnvironment } from './config/env'
import { loadRuntimeConfig } from './config/runtime'

// Initialize i18n and create app
async function initApp() {
  await loadRuntimeConfig()
  validateEnvironment()
  const router = createAppRouter()
  await setupI18n()

  // Setup mocks if enabled
  if (shouldEnableMocks()) {
    const mocks = await setupMocks()
    await mocks.start()
    console.log('🎭 MSW mocks enabled')
  }

  // Initialize API client with environment configuration
  initializeApiClient({
    baseUrl: env.API_BASE_URL,
    timeout: env.API_TIMEOUT,
  })

  const app = createApp(App)

  app.use(createPinia())
  app.use(router)
  app.use(i18n)
  app.use(VueQueryPlugin, {
    queryClientConfig: {
      defaultOptions: {
        queries: {
          staleTime: 5 * 60 * 1000, // 5 minutes
          retry: 3,
          retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
          refetchOnWindowFocus: false,
        },
        mutations: {
          retry: 1,
        },
      },
    },
  })
  // Get current locale and create PrimeVue locale config
  const currentLocale = getCurrentLocale()
  const primeVueLocale = createPrimeVueLocaleConfig(currentLocale)

  app.use(PrimeVue, {
    theme: {
      preset: YubiqPreset,
      options: {
        darkModeSelector: 'light',
        cssLayer: false,
      },
    },
    locale: primeVueLocale,
  })

  // PrimeVue only takes a locale as an install option, so keep it in step with vue-i18n:
  // the user's culture arrives from the API (see authService) after the app is mounted,
  // and without this the date pickers would stay in the browser-detected language
  const primeVue = app.config.globalProperties.$primevue
  watch(
    () => getCurrentLocale(),
    (locale) => applyPrimeVueLocale(primeVue, locale),
  )

  app.use(ConfirmationService)
  app.use(ToastService)
  app.directive('tooltip', Tooltip)
  app.directive('ripple', Ripple)

  app.mount('#app')
}

// Start the application
initApp().catch(console.error)
